import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SessionEventsChart from "../../components/charts/SessionEventsChart";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import { getIndividualSessionDetails } from "./individualService";
import { formatDateTime, formatDuration } from "../../utils/date";
import {
  createSessionNote,
  deleteSessionNote,
  deleteSessionNoteImage,
  getSessionNotes,
  updateSessionNote,
  uploadSessionNoteImage,
} from "../teams/sessionNotesService";
import NoteCard from "../../components/notes/NoteCard";

const IndividualDetailPage = () => {
  const { publicId } = useParams();
  const api = useApi();
  const { token, user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesData, setNotesData] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);

  const resolveSessionEvents = (data) => {
    if (!data) return [];
    if (Array.isArray(data.session?.events)) return data.session.events;
    if (Array.isArray(data.session?.eventData)) return data.session.eventData;
    if (Array.isArray(data.session?.sessionData?.events))
      return data.session.sessionData.events;
    if (Array.isArray(data.events)) return data.events;
    if (Array.isArray(data.eventData)) return data.eventData;
    if (Array.isArray(data.data?.events)) return data.data.events;
    if (Array.isArray(data.data?.eventData)) return data.data.eventData;
    if (Array.isArray(data.sessionData)) return data.sessionData;
    if (Array.isArray(data.sessionData?.events)) return data.sessionData.events;
    if (Array.isArray(data.events?.content)) return data.events.content;
    return [];
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token || !publicId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getIndividualSessionDetails(api, publicId);
        if (!mounted) return;
        setSession(data);
      } catch (err) {
        if (!mounted) return;
        setSession(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, publicId]);

  const handleUpdateNote = async (id, description) => {
    await updateSessionNote(api, id, description, false);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id ? { ...n, description } : n,
            ),
          }
        : prev,
    );
  };

  const handleUploadImage = async (id, file) => {
    const uploaded = await uploadSessionNoteImage(api, id, file);
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === id
                ? { ...n, files: [...(n.files || []), uploaded] }
                : n,
            ),
          }
        : prev,
    );
  };

  const handleDeleteNote = async (id) => {
    await deleteSessionNote(api, id);
    setNotesData((prev) =>
      prev ? { ...prev, notes: prev.notes.filter((n) => n.id !== id) } : prev,
    );
  };

  const handleDeleteImage = async (noteId, imageId, imageUrl) => {
    let prevState;
    setNotesData((prev) => {
      prevState = prev;
      return prev
        ? {
            ...prev,
            notes: prev.notes.map((n) =>
              n.id === noteId
                ? {
                    ...n,
                    files: (n.files || []).filter((f) =>
                      imageId
                        ? f.id !== imageId && f.imageId !== imageId
                        : f.url !== imageUrl,
                    ),
                  }
                : n,
            ),
          }
        : prev;
    });
    try {
      if (!imageId) return;
      await deleteSessionNoteImage(api, noteId, imageId);
    } catch {
      if (prevState) setNotesData(prevState);
    }
  };

  const handleAddNote = async () => {
    const created = await createSessionNote(
      api,
      publicId,
      "INDIVIDUAL",
      "",
      false,
    );
    const newNote = {
      id: created?.id,
      description: "",
      user: user || null,
      belongsToCurrentUser: true,
      createdAt: new Date().toISOString(),
      files: [],
      __autoEdit: true,
    };
    setNotesData((prev) =>
      prev
        ? { ...prev, notes: [newNote, ...(prev.notes || [])] }
        : { notes: [newNote] },
    );
  };

  const openNotesModal = () => {
    setNotesData((prev) =>
      prev
        ? {
            ...prev,
            notes: (prev.notes || []).map((n) =>
              n.__autoEdit ? { ...n, __autoEdit: false } : n,
            ),
          }
        : prev,
    );
    setShowNotesModal(true);
  };

  useEffect(() => {
    let mounted = true;
    const loadNotes = async () => {
      if (!token || !publicId) return;
      try {
        const data = await getSessionNotes(api, publicId, "INDIVIDUAL");
        if (!mounted) return;
        setNotesData(data);
      } catch {
        if (!mounted) return;
        setNotesData(null);
      }
    };
    loadNotes();
    return () => {
      mounted = false;
    };
  }, [api, token, publicId]);

  const formatSessionTitle = () => {
    const title = String(session?.title || "").trim();
    if (title) return title;
    if (!session?.createdAt)
      return session?.place ? session.place : "Session";
    const date = new Date(session.createdAt);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    const place = session?.place ? ` ${session.place}` : "";
    return `${day} ${month}, ${year}${place}`;
  };

  return (
    <div>
      <div className="detail-header">
        <div className="detail-header-title">{formatSessionTitle()}</div>
        <div className="detail-header-meta">
          <img
            src={user?.profilePictureUrl || "/icons/Logo_PNG.png"}
            alt={user?.username || "User"}
          />
          <span>{user?.username || "User"}</span>
        </div>
      </div>
      {loading && <Loader />}
      {!loading && !session && (
        <EmptyState title="No data" message="Session not found." />
      )}
      {!loading && session && (
        <div className="detail-stack">
          <div className="detail-split">
            <div className="card detail-meta">
              <div>Session Title: {formatSessionTitle()}</div>
              <div>
                Session Notes:{" "}
                {notesData?.notes ? notesData.notes.length : "—"}
              </div>
              <div>Place: {session.place || "-"}</div>
              <div>Started: {formatDateTime(session.createdAt)}</div>
              <div>Ended: {formatDateTime(session.endedAt)}</div>
              <div>
                Duration: {formatDuration(session.createdAt, session.endedAt)}
              </div>
            </div>
            <div className="card detail-notes">
              <div className="detail-notes-title">Notes</div>
              {!notesData?.notes?.length && (
                <div className="note-empty">
                  No notes yet.
                  <button
                    type="button"
                    className="btn btn-primary notes-add"
                    onClick={handleAddNote}
                  >
                    Add note
                  </button>
                </div>
              )}
              {notesData?.notes?.length > 0 && (
                <>
                  <div
                    className="notes-list notes-compact"
                    onClick={openNotesModal}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        openNotesModal();
                      }
                    }}
                  >
                    {notesData.notes.slice(0, 2).map((note) => (
                      <NoteCard
                        key={note.id}
                        note={note}
                        canEdit={note.belongsToCurrentUser}
                        compact
                        onUpdate={handleUpdateNote}
                        onUploadImage={handleUploadImage}
                        onDelete={handleDeleteNote}
                        onDeleteImage={handleDeleteImage}
                        modalOnEdit
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <SessionEventsChart
            events={resolveSessionEvents(session)}
            userName={user?.username}
            avatar={user?.profilePictureUrl}
          />
        </div>
      )}
      {showNotesModal && (
        <div className="note-modal-backdrop">
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note-modal-header">
              <div className="note-modal-title">All Notes</div>
              <button
                type="button"
                className="note-modal-close"
                onClick={() => setShowNotesModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="note-modal-body">
              {(notesData?.notes || []).map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  canEdit={note.belongsToCurrentUser}
                  onUpdate={handleUpdateNote}
                  onUploadImage={handleUploadImage}
                  onDelete={handleDeleteNote}
                  onDeleteImage={handleDeleteImage}
                  modalOnEdit
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualDetailPage;
